import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import * as React from "react";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct;

export interface NISProductDetailsPageTitleStyles {
    container?: GridContainerProps;
    brandGridItem?: GridItemProps;
    brandStyles?: ProductBrandStyles;
    titleGridItem?: GridItemProps;
    titleText?: TypographyProps;
}

export const nisPageTitleStyles: NISProductDetailsPageTitleStyles = {
    container: {
        gap: 0,
        offsetCss: css`
            max-width: 100%;
            gap: 3px;
        `,
    },
    brandGridItem: {
        width: 12,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-width: max-content;
                        z-index: 1;
                    `,
                    css`
                        max-width: max-content;
                        z-index: 1;
                    `,
                    css``,
                    null,
                    null,
                ])}
        `,
    },
    brandStyles: {
        logoImage: {
            css: css`
                img {
                    max-width: 150px;
                    max-height: 150px;
                }
            `,
        },
        nameText: {
            size: 18,
            weight: 600,
            css: css`
                span {
                    color: #000000 !important;
                }
                color: #000000 !important;
                font-family: Roboto Condensed;
            `,
        },
        nameLink: {
            css: css`
                span {
                    color: #000000 !important;
                }
                font-family: Roboto Condensed;
            `,
        },
    },
    titleGridItem: {
        width: 12,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    css`
                        padding-right: 104px;
                    `,
                    css`
                        padding-right: 104px;
                    `,
                ])}
        `,
    },
    titleText: {
        variant: "h1",
        css: css`
            color: ${getColor("primary")};
            font-weight: 600;
            font-family: Roboto Condensed;
            line-height: 32px;
            font-size: 24px !important;
            line-height: 24px !important;
            font-weight: ${TYPOGRAPHY.weight.mediumbold} !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            color: ${getColor("primary")};
                            font-weight: 600;
                            font-family: Roboto Condensed;
                            line-height: 24px;
                            font-size: 18px !important;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
            @media (min-width:991px) and (max-width:1024px) {
                color: ${getColor("primary")};
                font-weight: 600;
                font-family: Roboto Condensed;
                line-height: 24px;
                font-size: 18px !important;
            }
        `,
    },
};

const styles = nisPageTitleStyles;

const NISProductDetailsPageTitle: React.FC<Props> = ({ product }) => {
    return (
        <GridContainer {...styles.container}>
            {product.brand && (
                <GridItem {...styles.brandGridItem}>
                    <ProductBrand brand={product.brand} showLogo={false} extendedStyles={styles.brandStyles} />
                </GridItem>
            )}
            <GridItem {...styles.titleGridItem}>
                <Typography {...styles.titleText} data-test-selector="productDetails_productDescription">
                    {product.productTitle}
                </Typography>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(NISProductDetailsPageTitle),
    definition: {
        displayName: "NIS Page Title",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
